/**
 * Start a draft invoice.
 *
 * Four fields, because a draft is deliberately empty: the client, an optional
 * project, the tax rate and a note. What goes on it is decided on the invoice
 * itself, where the approved time and the outstanding expenses can be seen.
 *
 * The tax rate is fixed at this moment and stored on the invoice. A rate change
 * next quarter must not restate a document a client already has.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  RecordSelect,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

const schema = z.object({
  tax_rate: z
    .string()
    .trim()
    .regex(/^\d{1,2}(\.\d{1,2})?$/, 'A percentage, to two decimals.')
    .refine((value) => Number(value) <= 99, 'That cannot be right.'),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = { tax_rate: '18.00', notes: '' };

export function NewInvoiceDialog({
  open,
  onClose,
  onCreated,
  clientPublicId,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
  clientPublicId?: string;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [client, setClient] = useState<string | null>(clientPublicId ?? null);
  const [project, setProject] = useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULTS);
    setClient(clientPublicId ?? null);
    setProject(null);
  }, [open, form, clientPublicId]);

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ public_id: string; code: string }>(
        `/finance/invoices/for-client/${client}`,
        {
          project_id: project,
          tax_rate: values.tax_rate,
          notes: values.notes?.trim() || null,
        },
      ),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['finance'] });
      toast.success('Draft created', record.code);
      onCreated?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (client === null) {
      form.setError('root', { message: 'Choose the client to bill.' });
      return;
    }
    void form.handleSubmit((values) => create.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New invoice"
      description="Starts as an empty draft. Nothing is sent until you issue it."
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} onClick={submit}>
            Create draft
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <RecordSelect
          label="Client"
          required
          endpoint="/crm/clients"
          value={client}
          valueLabel={clientName}
          onChange={(next) => {
            setClient(next);
            setProject(null);
          }}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.code ?? ''),
          })}
          placeholder="Search clients…"
          hint="The currency and payment terms come from the client."
        />

        <RecordSelect
          label="Project"
          endpoint="/projects"
          value={project}
          onChange={(next) => setProject(next)}
          params={client ? { client } : undefined}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.client_name ?? ''),
          })}
          placeholder="Across all projects"
          hint="Naming one confines the time this invoice can pull in."
          disabled={client === null}
        />

        <Input
          label="Tax rate (%)"
          required
          inputMode="decimal"
          hint="Fixed on the invoice now, so a later change cannot restate it."
          error={form.formState.errors.tax_rate?.message}
          {...form.register('tax_rate')}
        />

        <Textarea
          label="Notes"
          rows={2}
          hint="Printed on the invoice. Payment instructions, a purchase-order number."
          error={form.formState.errors.notes?.message}
          {...form.register('notes')}
        />
      </form>
    </Modal>
  );
}
